import {
  ContractDraftStatus,
  GenerateContractDraftError,
} from "../../../../types/onboarding";
import { stateLabel } from "../../../../utils/onboardingStates";

// Humanize a backend field identifier for display (no raw snake_case in copy).
const humanizeField = (name: string): string =>
  name
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

/**
 * Map the backend's structured contract-draft rejection into a clear,
 * non-technical message for ops (FR-006/FR-007). Falls back to a generic
 * retry message when the body has no specifics (template missing / storage
 * unavailable / unknown).
 */
export const contractDraftErrorMessage = (err: any): string => {
  const body: GenerateContractDraftError | undefined = err?.response?.data;

  if (body?.missing_fields?.length) {
    const fields = body.missing_fields.map(humanizeField).join(", ");
    return `Can't generate the draft — required data is missing: ${fields}.`;
  }

  if (body?.invalid_fields?.length) {
    const parts = body.invalid_fields.map(
      (f) => `${humanizeField(f.field)} (${f.reason})`
    );
    return `Can't generate the draft — please fix: ${parts.join("; ")}.`;
  }

  if (body?.required_states?.length) {
    const need = body.required_states.map(stateLabel).join(" or ");
    return `Can't generate the draft yet — the prospect must first reach: ${need}.`;
  }

  if (body?.error === "DRAFT_GENERATION_IN_PROGRESS") {
    return "A contract draft is already being generated for this prospect. It will appear here when it's ready.";
  }

  if (body?.error === "DRAFT_GENERATION_UNAVAILABLE") {
    return "Contract draft generation couldn't be started. Please try again in a moment.";
  }

  return GENERIC_FAILURE;
};

const GENERIC_FAILURE = "Could not generate the contract draft. Please try again.";

/**
 * Map a background generation failure (CWA-187: the status endpoint's
 * error_code) to a message that tells ops whether to retry or escalate.
 */
export const contractDraftFailureMessage = (
  status: Pick<ContractDraftStatus, "status" | "error_code" | "error_message">
): string => {
  switch (status.error_code) {
    case "CONTRACT_TEMPLATE_NOT_FOUND":
      return "The contract template is missing or can't be read. Please contact tech — generating again won't help.";
    case "DOCUMENT_STORAGE_UNAVAILABLE":
      return "Google Drive was unavailable while generating the draft. Please try again in a moment.";
    case "DISPATCH_FAILED":
      return "Contract draft generation couldn't be started. Please try again in a moment.";
    case "PROSPECT_NOT_READY":
      return `The prospect was no longer ready for a draft when it was generated${
        status.error_message ? `: ${status.error_message}` : "."
      } Please check the prospect and try again.`;
    default:
      return GENERIC_FAILURE;
  }
};
